package com.speedsoftware.nibbler.ui.auth

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.speedsoftware.nibbler.client.NibblerApiClient
import com.speedsoftware.nibbler.models.ResetPasswordRequest
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.Screen
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

enum class ResetStep { EMAIL, CHECK_EMAIL, CODE, SUCCESS }

class ForgotPasswordFlow(
    private val appState: AppState,
    private val scope: CoroutineScope,
    urlToken: String? = null
) {
    var step by mutableStateOf(if (urlToken != null) ResetStep.CODE else ResetStep.EMAIL)
        private set
    var isLoading by mutableStateOf(false)
        private set
    var errorMessage by mutableStateOf<String?>(null)

    private var urlToken: String? = urlToken
    var email by mutableStateOf("")

    fun submitEmail() {
        if (isLoading) return
        if (email.isBlank()) {
            errorMessage = "Enter your email address"
            return
        }
        isLoading = true
        errorMessage = null
        scope.launch {
            appState.apiClient.forgotPassword(email.trim())
                .onSuccess {
                    step = ResetStep.CHECK_EMAIL
                }
                .onFailure {
                    errorMessage = describe(it)
                }
            isLoading = false
        }
    }

    fun submitReset(code: String, newPassword: String, confirmPassword: String) {
        if (isLoading) return
        if (urlToken == null && code.isBlank()) {
            errorMessage = "Enter the code from your email"
            return
        }
        if (newPassword.length < 8) {
            errorMessage = "Password must be at least 8 characters"
            return
        }
        if (newPassword != confirmPassword) {
            errorMessage = "Passwords do not match"
            return
        }
        isLoading = true
        errorMessage = null
        val request = if (urlToken != null) {
            ResetPasswordRequest(token = urlToken, newPassword = newPassword)
        } else {
            ResetPasswordRequest(email = email.trim(), code = code.trim(), newPassword = newPassword)
        }
        scope.launch {
            appState.apiClient.resetPassword(request)
                .onSuccess { step = ResetStep.SUCCESS }
                .onFailure { errorMessage = describe(it) }
            isLoading = false
        }
    }

    fun enterCodeManually() {
        errorMessage = null
        step = ResetStep.CODE
    }

    fun backToLogin() {
        appState.navigateTo(Screen.Login)
    }

    private fun describe(error: Throwable): String = when (error) {
        is NibblerApiClient.ApiException -> error.message ?: "Request failed"
        else -> "Could not reach the server. Check your connection."
    }
}
